import { useState, useEffect, useCallback } from 'react';
import { Spinner } from '../ui';
import { useWebshopStore } from '../../stores';
import type { WebshopOrder } from '../../lib/salesforce';

interface EditAddressDialogProps {
  order: WebshopOrder | null;
  isOpen: boolean;
  onClose: () => void;
}

const COUNTRY_OPTIONS = [
  { code: 'NL', label: 'Nederland' },
  { code: 'BE', label: 'België' },
];

interface PostalRule {
  regex: RegExp;
  placeholder: string;
  hint: string;
}

const POSTAL_RULE_NL: PostalRule = { regex: /^\d{4}\s?[A-Z]{2}$/i, placeholder: '3511AB', hint: 'Nederlands formaat: 4 cijfers + 2 letters' };
const POSTAL_RULE_BE: PostalRule = { regex: /^\d{4}$/, placeholder: '1000', hint: 'Belgisch formaat: 4 cijfers' };

function getPostalRule(countryCode: string): PostalRule {
  if (countryCode === 'BE') return POSTAL_RULE_BE;
  return POSTAL_RULE_NL;
}

export function EditAddressDialog({ order, isOpen, onClose }: EditAddressDialogProps) {
  const { updateAddress, isUpdating } = useWebshopStore();

  const [street, setStreet] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [countryCode, setCountryCode] = useState('NL');
  const [error, setError] = useState<string | null>(null);
  const [postalHint, setPostalHint] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!order || !isOpen) return;
    setStreet(order.street ?? '');
    setPostalCode(order.postalCode ?? '');
    setCity(order.city ?? '');
    setCountryCode(order.country || 'NL');
    setError(null);
    setPostalHint(null);
    setSaved(false);
  }, [order, isOpen]);

  const postalRule = getPostalRule(countryCode);

  function checkPostalFormat(value: string) {
    const trimmed = value.trim().replace(/\s/g, '');
    if (!trimmed) {
      setPostalHint(null);
      return;
    }
    if (!postalRule.regex.test(trimmed)) {
      setPostalHint(`Let op: ${postalRule.hint}`);
    } else {
      setPostalHint(null);
    }
  }

  const handleSave = useCallback(async () => {
    if (!order) return;
    const trimmedStreet = street.trim();
    const trimmedPostal = postalCode.trim();
    const trimmedCity = city.trim();
    if (!trimmedStreet || !trimmedPostal || !trimmedCity || !countryCode) return;

    setError(null);
    try {
      await updateAddress(order.id, {
        street: trimmedStreet,
        postalCode: trimmedPostal,
        city: trimmedCity,
        countryCode,
      });
      setSaved(true);
      setTimeout(() => onClose(), 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
    }
  }, [order, street, postalCode, city, countryCode, updateAddress, onClose]);

  if (!isOpen || !order) return null;

  const disabled = !street.trim() || !postalCode.trim() || !city.trim() || !countryCode;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Verzendadres wijzigen
        </h3>

        {saved ? (
          <div className="py-8 text-center">
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-900">Adres opgeslagen</p>
          </div>
        ) : (
          <>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Straat + huisnummer</label>
              <input
                type="text"
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                placeholder="Kerkstraat 12"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                <input
                  type="text"
                  value={postalCode}
                  onChange={(e) => {
                    setPostalCode(e.target.value);
                    if (postalHint) setPostalHint(null);
                  }}
                  onBlur={(e) => checkPostalFormat(e.target.value)}
                  placeholder={postalRule.placeholder}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                {postalHint && <p className="text-xs text-amber-700 mt-1">{postalHint}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stad</label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Utrecht"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Land</label>
              <select
                value={countryCode}
                onChange={(e) => {
                  setCountryCode(e.target.value);
                  setPostalHint(null);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
              >
                {COUNTRY_OPTIONS.map((opt) => (
                  <option key={opt.code} value={opt.code}>
                    {opt.code} — {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {error && (
              <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={isUpdating}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isUpdating || disabled}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isUpdating ? (
                  <>
                    <Spinner className="w-4 h-4" />
                    Opslaan...
                  </>
                ) : (
                  'Opslaan'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
